import { Suspense, lazy, useEffect, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';

import i18n from './i18n';
import { ProtectedRoute } from './components/ProtectedRoute';
import { useAuth } from './lib/auth';
import { SplashScreen } from './components/SplashScreen';
const AlertsPage = lazy(() => import('./pages/Alerts/AlertsPage').then((m) => ({ default: m.AlertsPage })));
const NotificationsPage = lazy(() => import('./pages/Notifications/NotificationsPage').then((m) => ({ default: m.NotificationsPage })));
const AuditLogPage = lazy(() => import('./pages/Audit/AuditLogPage').then((m) => ({ default: m.AuditLogPage })));
const ForecastPage = lazy(() => import('./pages/Forecast/ForecastPage').then((m) => ({ default: m.ForecastPage })));
const FlowForecastPage = lazy(() => import('./pages/Forecast/FlowForecastPage').then((m) => ({ default: m.FlowForecastPage })));
const SourcingPage = lazy(() => import('./pages/Sourcing/SourcingPage').then((m) => ({ default: m.SourcingPage })));
const StaffHomePage = lazy(() => import('./pages/Staff/StaffHome').then((m) => ({ default: m.StaffHomePage })));
const StaffProductsPage = lazy(() => import('./pages/Staff/StaffProducts').then((m) => ({ default: m.StaffProductsPage })));
const StaffShiftsPage = lazy(() => import('./pages/Staff/StaffShifts').then((m) => ({ default: m.StaffShiftsPage })));
const StaffNotificationsPage = lazy(() => import('./pages/Staff/StaffNotifications').then((m) => ({ default: m.StaffNotificationsPage })));
const StaffKitchenTasksPage = lazy(() => import('./pages/Staff/StaffKitchenTasks').then((m) => ({ default: m.StaffKitchenTasksPage })));
const KitchenTasksManagePage = lazy(() => import('./pages/KitchenTasks/Manage').then((m) => ({ default: m.KitchenTasksManagePage })));
const StaffDishWastePage = lazy(() => import('./pages/Staff/StaffDishWaste').then((m) => ({ default: m.StaffDishWastePage })));
const DishWasteManagePage = lazy(() => import('./pages/DishWaste/Manage').then((m) => ({ default: m.DishWasteManagePage })));
const StaffVacationsPage = lazy(() => import('./pages/Staff/StaffVacations').then((m) => ({ default: m.StaffVacationsPage })));
const VacationRequestsAdminPage = lazy(() => import('./pages/Personnel/VacationRequests').then((m) => ({ default: m.VacationRequestsAdminPage })));
const VacationWindowsPage = lazy(() => import('./pages/Settings/VacationWindows').then((m) => ({ default: m.VacationWindowsPage })));
const JobRolesPage = lazy(() => import('./pages/Settings/JobRoles').then((m) => ({ default: m.JobRolesPage })));
const TransfersListPage = lazy(() => import('./pages/Transfers/List').then((m) => ({ default: m.TransfersListPage })));
const TransferFormPage = lazy(() => import('./pages/Transfers/Form').then((m) => ({ default: m.TransferFormPage })));
const HelpPage = lazy(() => import('./pages/Help/HelpPage').then((m) => ({ default: m.HelpPage })));
const MenuEngineeringPage = lazy(() => import('./pages/Menu/MenuEngineering').then((m) => ({ default: m.MenuEngineeringPage })));
const CassaSalaPage = lazy(() => import('./pages/Cassa/Sala').then((m) => ({ default: m.CassaSalaPage })));
const CassaComandaPage = lazy(() => import('./pages/Cassa/Comanda').then((m) => ({ default: m.CassaComandaPage })));
const CassaKdsPage = lazy(() => import('./pages/Cassa/Kds').then((m) => ({ default: m.CassaKdsPage })));
const VoidsRegisterPage = lazy(() => import('./pages/Cassa/VoidsRegister').then((m) => ({ default: m.VoidsRegisterPage })));
const SettingsModulesPage = lazy(() => import('./pages/Settings/Modules').then((m) => ({ default: m.SettingsModulesPage })));
const RecipeEditorPage = lazy(() => import('./pages/Menu/RecipeEditor').then((m) => ({ default: m.RecipeEditorPage })));
const HomePage = lazy(() => import('./pages/Home').then((m) => ({ default: m.HomePage })));
const AcceptInvitePage = lazy(() => import('./pages/AcceptInvite').then((m) => ({ default: m.AcceptInvitePage })));
const BachecaPage = lazy(() => import('./pages/Talent/Bacheca').then((m) => ({ default: m.BachecaPage })));
const MyTalentProfilePage = lazy(() => import('./pages/Talent/MyTalentProfile').then((m) => ({ default: m.MyTalentProfilePage })));
const ReferenceRequestsPage = lazy(() => import('./pages/Talent/ReferenceRequests').then((m) => ({ default: m.ReferenceRequestsPage })));
const OpeningsFeedPage = lazy(() => import('./pages/Talent/OpeningsFeed').then((m) => ({ default: m.OpeningsFeedPage })));
const JobOpeningsPage = lazy(() => import('./pages/Talent/JobOpenings').then((m) => ({ default: m.JobOpeningsPage })));
const NetworkHubPage = lazy(() => import('./pages/Talent/NetworkHub').then((m) => ({ default: m.NetworkHubPage })));
const LoginPage = lazy(() => import('./pages/Login').then((m) => ({ default: m.LoginPage })));
const RegisterPage = lazy(() => import('./pages/Register').then((m) => ({ default: m.RegisterPage })));
const SuppliersListPage = lazy(() => import('./pages/Suppliers/List').then((m) => ({ default: m.SuppliersListPage })));
const SupplierFormPage = lazy(() => import('./pages/Suppliers/Form').then((m) => ({ default: m.SupplierFormPage })));
const ProductsListPage = lazy(() => import('./pages/Products/List').then((m) => ({ default: m.ProductsListPage })));
const ProductFormPage = lazy(() => import('./pages/Products/Form').then((m) => ({ default: m.ProductFormPage })));
const BatchesListPage = lazy(() => import('./pages/Batches/List').then((m) => ({ default: m.BatchesListPage })));
const BatchFormPage = lazy(() => import('./pages/Batches/Form').then((m) => ({ default: m.BatchFormPage })));
const MovementsListPage = lazy(() => import('./pages/Movements/List').then((m) => ({ default: m.MovementsListPage })));
const MovementFormPage = lazy(() => import('./pages/Movements/Form').then((m) => ({ default: m.MovementFormPage })));
const CashDashboardPage = lazy(() => import('./pages/Cash/Dashboard').then((m) => ({ default: m.CashDashboardPage })));
const CashSaleFormPage = lazy(() => import('./pages/Cash/SaleForm').then((m) => ({ default: m.CashSaleFormPage })));
const CashExpenseFormPage = lazy(() => import('./pages/Cash/ExpenseForm').then((m) => ({ default: m.CashExpenseFormPage })));
const EmployeesListPage = lazy(() => import('./pages/Personnel/EmployeesList').then((m) => ({ default: m.EmployeesListPage })));
const TeamRecordsPage = lazy(() => import('./pages/Personnel/TeamRecords').then((m) => ({ default: m.TeamRecordsPage })));
const TeamRecordDetailPage = lazy(() => import('./pages/Personnel/TeamRecords').then((m) => ({ default: m.TeamRecordDetailPage })));
const TimbraPage = lazy(() => import('./pages/TimeClock/Timbra').then((m) => ({ default: m.TimbraPage })));
const TimeClockManagePage = lazy(() => import('./pages/TimeClock/TimeClockManage').then((m) => ({ default: m.TimeClockManagePage })));
const EmployeeFormPage = lazy(() => import('./pages/Personnel/EmployeeForm').then((m) => ({ default: m.EmployeeFormPage })));
const ShiftsListPage = lazy(() => import('./pages/Personnel/ShiftsList').then((m) => ({ default: m.ShiftsListPage })));
const ShiftsCalendarPage = lazy(() => import('./pages/Personnel/ShiftsCalendar').then((m) => ({ default: m.ShiftsCalendarPage })));
const ShiftFormPage = lazy(() => import('./pages/Personnel/ShiftForm').then((m) => ({ default: m.ShiftFormPage })));
const ShiftServicesSettingsPage = lazy(() => import('./pages/Settings/ShiftServices').then((m) => ({ default: m.ShiftServicesSettingsPage })));
const PayrollPage = lazy(() => import('./pages/Personnel/PayrollPage').then((m) => ({ default: m.PayrollPage })));
const SettingsHubPage = lazy(() => import('./pages/Settings/Hub').then((m) => ({ default: m.SettingsHubPage })));
const SettingsProfilePage = lazy(() => import('./pages/Settings/Profile').then((m) => ({ default: m.SettingsProfilePage })));
const SettingsNotificationsPage = lazy(() => import('./pages/Settings/Notifications').then((m) => ({ default: m.SettingsNotificationsPage })));
const SettingsCompanyPage = lazy(() => import('./pages/Settings/Company').then((m) => ({ default: m.SettingsCompanyPage })));
const SettingsLocationsPage = lazy(() => import('./pages/Settings/Locations').then((m) => ({ default: m.SettingsLocationsPage })));
const SettingsMembersPage = lazy(() => import('./pages/Settings/Members').then((m) => ({ default: m.SettingsMembersPage })));
const SettingsInvitesPage = lazy(() => import('./pages/Settings/Invites').then((m) => ({ default: m.SettingsInvitesPage })));
const SettingsDepartmentsPage = lazy(() => import('./pages/Settings/Departments').then((m) => ({ default: m.SettingsDepartmentsPage })));
const InvoicesListPage = lazy(() => import('./pages/Invoices/List').then((m) => ({ default: m.InvoicesListPage })));
const InvoiceUploadPage = lazy(() => import('./pages/Invoices/Upload').then((m) => ({ default: m.InvoiceUploadPage })));
const InvoiceDetailPage = lazy(() => import('./pages/Invoices/Detail').then((m) => ({ default: m.InvoiceDetailPage })));
const ContractsListPage = lazy(() => import('./pages/Contracts/List').then((m) => ({ default: m.ContractsListPage })));
const ContractFormPage = lazy(() => import('./pages/Contracts/Form').then((m) => ({ default: m.ContractFormPage })));
const HaccpDashboardPage = lazy(() => import('./pages/Haccp/Dashboard').then((m) => ({ default: m.HaccpDashboardPage })));
const EquipmentListPage = lazy(() => import('./pages/Haccp/EquipmentList').then((m) => ({ default: m.EquipmentListPage })));
const EquipmentFormPage = lazy(() => import('./pages/Haccp/EquipmentForm').then((m) => ({ default: m.EquipmentFormPage })));
const CleaningDashboardPage = lazy(() => import('./pages/Haccp/CleaningDashboard').then((m) => ({ default: m.CleaningDashboardPage })));
const CleaningTasksListPage = lazy(() => import('./pages/Haccp/CleaningTasksList').then((m) => ({ default: m.CleaningTasksListPage })));
const HaccpAuditPage = lazy(() => import('./pages/Haccp/Audit').then((m) => ({ default: m.HaccpAuditPage })));
const HaccpManualPage = lazy(() => import('./pages/Haccp/Manual').then((m) => ({ default: m.HaccpManualPage })));
const SensorsPage = lazy(() => import('./pages/Haccp/Sensors').then((m) => ({ default: m.SensorsPage })));
const BlastChillingPage = lazy(() => import('./pages/Haccp/BlastChilling').then((m) => ({ default: m.BlastChillingPage })));
const MenuItemsListPage = lazy(() => import('./pages/Menu/List').then((m) => ({ default: m.MenuItemsListPage })));
const MultiLocationDashboardPage = lazy(() => import('./pages/MultiLocation/Dashboard').then((m) => ({ default: m.MultiLocationDashboardPage })));
const HoldingHubPage = lazy(() => import('./pages/Holding/HoldingHub').then((m) => ({ default: m.HoldingHubPage })));
const HoldingDashboardPage = lazy(() => import('./pages/Holding/Dashboard').then((m) => ({ default: m.HoldingDashboardPage })));
const MyCompaniesPage = lazy(() => import('./pages/Account/MyCompanies').then((m) => ({ default: m.MyCompaniesPage })));
const PayrollSimulatorPage = lazy(() => import('./pages/Payroll/Simulator').then((m) => ({ default: m.PayrollSimulatorPage })));
const CostsDashboardPage = lazy(() => import('./pages/Analytics/CostsDashboard').then((m) => ({ default: m.CostsDashboardPage })));
const SalesDashboardPage = lazy(() => import('./pages/Analytics/SalesDashboard').then((m) => ({ default: m.SalesDashboardPage })));
const AdminErrorsPage = lazy(() => import('./pages/Admin/Errors').then((m) => ({ default: m.AdminErrorsPage })));
const CategoryPage = lazy(() => import('./pages/CategoryPage').then((m) => ({ default: m.CategoryPage })));
const SuitePage = lazy(() => import('./pages/SuitePage').then((m) => ({ default: m.SuitePage })));
const ScadenzarioPage = lazy(() => import('./pages/Payments/Scadenzario').then((m) => ({ default: m.ScadenzarioPage })));
const ReservationsPage = lazy(() => import('./pages/Reservations/List').then((m) => ({ default: m.ReservationsPage })));
const DepositOkPage = lazy(() => import('./pages/Reservations/DepositResult').then((m) => ({ default: m.DepositOkPage })));
const DepositCanceledPage = lazy(() => import('./pages/Reservations/DepositResult').then((m) => ({ default: m.DepositCanceledPage })));
const PaymentsSettingsPage = lazy(() => import('./pages/Settings/Payments').then((m) => ({ default: m.PaymentsSettingsPage })));
const ClosuresPage = lazy(() => import('./pages/Settings/Closures').then((m) => ({ default: m.ClosuresPage })));
const PosCatalogPage = lazy(() => import('./pages/Settings/PosCatalog').then((m) => ({ default: m.PosCatalogPage })));
const PosClosuresPage = lazy(() => import('./pages/Settings/PosClosures').then((m) => ({ default: m.PosClosuresPage })));
const RolesPage = lazy(() => import('./pages/Settings/Roles').then((m) => ({ default: m.RolesPage })));
const PriceListDetailPage = lazy(() => import('./pages/PriceLists/PriceListDetail').then((m) => ({ default: m.PriceListDetailPage })));
const SupplierPriceListsPage = lazy(() => import('./pages/PriceLists/SupplierPriceLists').then((m) => ({ default: m.SupplierPriceListsPage })));
const ReviewsConfigPage = lazy(() => import('./pages/Reviews/Config').then((m) => ({ default: m.ReviewsConfigPage })));
const ReviewsListPage = lazy(() => import('./pages/Reviews/List').then((m) => ({ default: m.ReviewsListPage })));
const TraceabilityBatchPage = lazy(() => import('./pages/Traceability/Search').then((m) => ({ default: m.TraceabilityBatchPage })));
const TraceabilitySearchPage = lazy(() => import('./pages/Traceability/Search').then((m) => ({ default: m.TraceabilitySearchPage })));

/**
 * Listener globale: quando l'API client emette `auth:unauthorized`
 * (token scaduto o membership invalidata), redirigiamo a /login.
 */
function UnauthorizedListener() {
  const navigate = useNavigate();
  useEffect(() => {
    const handler = () => navigate('/login', { replace: true });
    window.addEventListener('auth:unauthorized', handler);
    return () => window.removeEventListener('auth:unauthorized', handler);
  }, [navigate]);
  return null;
}

const SPLASH_SESSION_KEY = 'manteco-splash-shown';
const SPLASH_HOLD_MS = 1700;

/**
 * Mostra il SplashScreen una sola volta per session.
 * sessionStorage: persiste tra hard reload nella stessa scheda,
 *                 reset apertura di una nuova scheda / browser.
 */
function useSplashOnce() {
  const [showSplash, setShowSplash] = useState(() => {
    if (typeof window === 'undefined') return false;
    try {
      return !window.sessionStorage.getItem(SPLASH_SESSION_KEY);
    } catch {
      return true; // se sessionStorage è bloccato, mostra comunque
    }
  });

  useEffect(() => {
    if (!showSplash) return;
    const t = window.setTimeout(() => {
      try {
        window.sessionStorage.setItem(SPLASH_SESSION_KEY, '1');
      } catch {
        /* noop */
      }
      setShowSplash(false);
    }, SPLASH_HOLD_MS);
    return () => window.clearTimeout(t);
  }, [showSplash]);

  return showSplash;
}

// Version marker: utile per diagnosticare se il bundle JS in cache nel
// browser e' aggiornato o no. Apri DevTools → Console e cerca questa
// riga: se la vedi, sei sull'ultima build.
// eslint-disable-next-line no-console
console.log('%c[Manteco] build 2026-06-26t · notif-prefs-member', 'color:#C23535;font-weight:600');

/**
 * Top-level guard: lo staff puo' vivere SOLO dentro /staff/*. Qualsiasi
 * tentativo di navigare altrove (URL diretto, link condiviso, back
 * browser) viene intercettato qui prima ancora che le Routes vengano
 * matchate. Defense in depth oltre a ProtectedRoute e AppShell.
 */
function StaffGate() {
  const { me } = useAuth();
  const location = useLocation();
  if (!me) return null;
  // Diagnostic log (solo se utente staff): vediamo in console se la
  // guard sta venendo eseguita e con quale role.
  if (me.role === 'staff') {
    // eslint-disable-next-line no-console
    console.log(
      `[Manteco StaffGate] me.role="${me.role}" pathname="${location.pathname}"`,
    );
  }
  if (me.role !== 'staff') return null;
  const p = location.pathname;
  if (p === '/staff' || p.startsWith('/staff/') || p === '/login' || p === '/register') {
    return null;
  }
  return <Navigate to="/staff" replace />;
}

/**
 * Applica la lingua salvata sull'account: quando /auth/me arriva con
 * `user.language`, quella vince sul localStorage (è la preferenza che segue
 * l'utente cross-device). Montato una sola volta in App.
 */
function AccountLanguageSync() {
  const { me } = useAuth();
  const lang = me?.user.language;
  useEffect(() => {
    if (lang && i18n.resolvedLanguage !== lang) {
      void i18n.changeLanguage(lang);
    }
  }, [lang]);
  return null;
}

export function App() {
  const showSplash = useSplashOnce();
  return (
    <>
      <AnimatePresence>
        {showSplash && <SplashScreen />}
      </AnimatePresence>
      <BrowserRouter>
        <UnauthorizedListener />
        <AccountLanguageSync />
        <StaffGate />
        <Suspense
          fallback={
            <div className="flex min-h-screen items-center justify-center">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-muted border-t-foreground" />
            </div>
          }
        >
        <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/accetta-invito" element={<AcceptInvitePage />} />
        <Route path="/caparra/ok" element={<DepositOkPage />} />
        <Route path="/caparra/annullata" element={<DepositCanceledPage />} />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <HomePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/bacheca"
          element={
            <ProtectedRoute>
              <BachecaPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profilo-bacheca"
          element={
            <ProtectedRoute>
              <MyTalentProfilePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/staff/profilo-bacheca"
          element={
            <ProtectedRoute>
              <MyTalentProfilePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/referenze-richieste"
          element={
            <ProtectedRoute>
              <ReferenceRequestsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/lavoro"
          element={
            <ProtectedRoute>
              <NetworkHubPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/posizioni-vacanti"
          element={
            <ProtectedRoute>
              <JobOpeningsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/posizioni"
          element={
            <ProtectedRoute>
              <OpeningsFeedPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/staff/posizioni"
          element={
            <ProtectedRoute>
              <OpeningsFeedPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/errori"
          element={
            <ProtectedRoute>
              <AdminErrorsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/scadenzario"
          element={
            <ProtectedRoute>
              <ScadenzarioPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/prenotazioni"
          element={
            <ProtectedRoute>
              <ReservationsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/compiti-cucina"
          element={
            <ProtectedRoute>
              <KitchenTasksManagePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/resi-cucina"
          element={
            <ProtectedRoute>
              <DishWasteManagePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/cassa"
          element={
            <ProtectedRoute>
              <CassaSalaPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/cassa/comanda/:orderId"
          element={
            <ProtectedRoute>
              <CassaComandaPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/kds"
          element={
            <ProtectedRoute>
              <CassaKdsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/registro-storni"
          element={
            <ProtectedRoute>
              <VoidsRegisterPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/categoria/:slug"
          element={
            <ProtectedRoute>
              <CategoryPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/suite/:code"
          element={
            <ProtectedRoute>
              <SuitePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/suppliers"
          element={
            <ProtectedRoute>
              <SuppliersListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/suppliers/nuovo"
          element={
            <ProtectedRoute>
              <SupplierFormPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/suppliers/:id"
          element={
            <ProtectedRoute>
              <SupplierFormPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/products"
          element={
            <ProtectedRoute>
              <ProductsListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/products/nuovo"
          element={
            <ProtectedRoute>
              <ProductFormPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/products/:id"
          element={
            <ProtectedRoute>
              <ProductFormPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/batches"
          element={
            <ProtectedRoute>
              <BatchesListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/batches/nuovo"
          element={
            <ProtectedRoute>
              <BatchFormPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/batches/:id"
          element={
            <ProtectedRoute>
              <BatchFormPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/movements"
          element={
            <ProtectedRoute>
              <MovementsListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/movements/nuovo"
          element={
            <ProtectedRoute>
              <MovementFormPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/movements/:id"
          element={
            <ProtectedRoute>
              <MovementFormPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/cash"
          element={
            <ProtectedRoute>
              <CashDashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/cash/sales/nuova"
          element={
            <ProtectedRoute>
              <CashSaleFormPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/cash/sales/:id"
          element={
            <ProtectedRoute>
              <CashSaleFormPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/cash/expenses/nuova"
          element={
            <ProtectedRoute>
              <CashExpenseFormPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/cash/expenses/:id"
          element={
            <ProtectedRoute>
              <CashExpenseFormPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/employees"
          element={
            <ProtectedRoute>
              <EmployeesListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/andamento-team"
          element={
            <ProtectedRoute>
              <TeamRecordsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/andamento-team/:id"
          element={
            <ProtectedRoute>
              <TeamRecordDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/timbra"
          element={
            <ProtectedRoute>
              <TimbraPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/timbrature"
          element={
            <ProtectedRoute>
              <TimeClockManagePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/employees/:id"
          element={
            <ProtectedRoute>
              <EmployeeFormPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/segnalazioni"
          element={
            <ProtectedRoute>
              <AlertsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/notifiche"
          element={
            <ProtectedRoute>
              <NotificationsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/audit"
          element={
            <ProtectedRoute>
              <AuditLogPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/menu/:id/recipe"
          element={
            <ProtectedRoute>
              <RecipeEditorPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/menu-engineering"
          element={
            <ProtectedRoute>
              <MenuEngineeringPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/forecast"
          element={
            <ProtectedRoute>
              <ForecastPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/flusso"
          element={
            <ProtectedRoute>
              <FlowForecastPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/sourcing"
          element={
            <ProtectedRoute>
              <SourcingPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/staff"
          element={
            <ProtectedRoute>
              <StaffHomePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/staff/prodotti"
          element={
            <ProtectedRoute>
              <StaffProductsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/staff/turni"
          element={
            <ProtectedRoute>
              <StaffShiftsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/staff/notifiche"
          element={
            <ProtectedRoute>
              <StaffNotificationsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/staff/compiti"
          element={
            <ProtectedRoute>
              <StaffKitchenTasksPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/staff/resi"
          element={
            <ProtectedRoute>
              <StaffDishWastePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/turni"
          element={
            <ProtectedRoute>
              <ShiftsCalendarPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/shifts"
          element={
            <ProtectedRoute>
              <ShiftsCalendarPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/shifts/storico"
          element={
            <ProtectedRoute>
              <ShiftsListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/shifts/nuovo"
          element={
            <ProtectedRoute>
              <ShiftFormPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/shifts/:id"
          element={
            <ProtectedRoute>
              <ShiftFormPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/impostazioni/servizi-turni"
          element={
            <ProtectedRoute>
              <ShiftServicesSettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/payroll"
          element={
            <ProtectedRoute>
              <PayrollPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/payroll/simulator"
          element={
            <ProtectedRoute>
              <PayrollSimulatorPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/multi-location"
          element={
            <ProtectedRoute>
              <MultiLocationDashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/le-mie-aziende"
          element={
            <ProtectedRoute>
              <MyCompaniesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/holding"
          element={
            <ProtectedRoute>
              <HoldingHubPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/holding/dashboard"
          element={
            <ProtectedRoute>
              <HoldingDashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/menu"
          element={
            <ProtectedRoute>
              <MenuItemsListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/analytics/sales"
          element={
            <ProtectedRoute>
              <SalesDashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/analytics/costs"
          element={
            <ProtectedRoute>
              <CostsDashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/chiusure"
          element={
            <ProtectedRoute>
              <ClosuresPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/pos"
          element={
            <ProtectedRoute>
              <PosCatalogPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/pagamenti"
          element={
            <ProtectedRoute>
              <PaymentsSettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/pos/chiusure"
          element={
            <ProtectedRoute>
              <PosClosuresPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/roles"
          element={
            <ProtectedRoute>
              <RolesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/moduli"
          element={
            <ProtectedRoute>
              <SettingsModulesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/reviews"
          element={
            <ProtectedRoute>
              <ReviewsListPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/reviews/config"
          element={
            <ProtectedRoute>
              <ReviewsConfigPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/suppliers/:id/price-lists"
          element={
            <ProtectedRoute>
              <SupplierPriceListsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/price-lists/:id"
          element={
            <ProtectedRoute>
              <PriceListDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <SettingsHubPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/profile"
          element={
            <ProtectedRoute>
              <SettingsProfilePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/notifiche"
          element={
            <ProtectedRoute>
              <SettingsNotificationsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/company"
          element={
            <ProtectedRoute>
              <SettingsCompanyPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/locations"
          element={
            <ProtectedRoute>
              <SettingsLocationsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/members"
          element={
            <ProtectedRoute>
              <SettingsMembersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/inviti"
          element={
            <ProtectedRoute>
              <SettingsInvitesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/reparti"
          element={
            <ProtectedRoute>
              <SettingsDepartmentsPage />
            </ProtectedRoute>
          }
        />
          <Route
            path="/invoices"
            element={
              <ProtectedRoute>
                <InvoicesListPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/invoices/upload"
            element={
              <ProtectedRoute>
                <InvoiceUploadPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/invoices/:id"
            element={
              <ProtectedRoute>
                <InvoiceDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/contracts"
            element={
              <ProtectedRoute>
                <ContractsListPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/contracts/nuovo"
            element={
              <ProtectedRoute>
                <ContractFormPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/contracts/:id"
            element={
              <ProtectedRoute>
                <ContractFormPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/haccp"
            element={
              <ProtectedRoute>
                <HaccpDashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/haccp/equipment"
            element={
              <ProtectedRoute>
                <EquipmentListPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/haccp/sensori"
            element={
              <ProtectedRoute>
                <SensorsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/haccp/abbattimento"
            element={
              <ProtectedRoute>
                <BlastChillingPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/haccp/equipment/nuovo"
            element={
              <ProtectedRoute>
                <EquipmentFormPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/haccp/equipment/:id"
            element={
              <ProtectedRoute>
                <EquipmentFormPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/haccp/cleaning"
            element={
              <ProtectedRoute>
                <CleaningDashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/haccp/cleaning/tasks"
            element={
              <ProtectedRoute>
                <CleaningTasksListPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/haccp/manual"
            element={
              <ProtectedRoute>
                <HaccpManualPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/haccp/audit"
            element={
              <ProtectedRoute>
                <HaccpAuditPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/traceability"
            element={
              <ProtectedRoute>
                <TraceabilitySearchPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/traceability/batches/:id"
            element={
              <ProtectedRoute>
                <TraceabilityBatchPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/personale/ferie"
            element={
              <ProtectedRoute>
                <VacationRequestsAdminPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/finestre-ferie"
            element={
              <ProtectedRoute>
                <VacationWindowsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/ruoli-operativi"
            element={
              <ProtectedRoute>
                <JobRolesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/transfers"
            element={
              <ProtectedRoute>
                <TransfersListPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/transfers/nuovo"
            element={
              <ProtectedRoute>
                <TransferFormPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/staff/ferie"
            element={
              <ProtectedRoute>
                <StaffVacationsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/aiuto"
            element={
              <ProtectedRoute>
                <HelpPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/aiuto/categoria/:category"
            element={
              <ProtectedRoute>
                <HelpPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/aiuto/:slug"
            element={
              <ProtectedRoute>
                <HelpPage />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
      </BrowserRouter>
    </>
  );
}
